import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { EmployerService } from './employer.service';
import { ErrorHandlerService } from './error-handler.service';
import { tap } from 'rxjs/operators';
import { EmployerDto, FieldV2Dto, FieldRestService, FieldGeometryDto } from '../api/exa';
import { AuthService } from './auth.service';
import { NGXLogger } from 'ngx-logger';
import { ExaGrant } from '../model/exa-grant';
import { AlertService } from './alert.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class FieldService {
  private employerDto: EmployerDto = null;

  private fields: FieldV2Dto[] = [];
  private fields$ = new BehaviorSubject<FieldV2Dto[]>([]);
  private fieldsWD: FieldV2Dto[] = [];
  private fieldsWD$ = new BehaviorSubject<FieldV2Dto[]>([]);

  constructor(
    private access: AuthService,
    private employerService: EmployerService,
    private fieldRest: FieldRestService,
    private errorHandler: ErrorHandlerService,
    private alertService: AlertService,
    private translate: TranslateService,
    private log: NGXLogger
  ) {
    //  this.loadFields();
  }

  loadFields() {
    this.employerService.onSelectEmployer().subscribe((employer) => {
      this.employerDto = employer;

      if (this.employerDto) {
        this.reloadFields();
      } else {
        this.fieldsWD = [];
        this.fields = [];
        this.fieldsWD$.next(this.fieldsWD);
        this.fields$.next(this.fields);
      }
    });
    this.log.debug('Construct');
  }

  reloadFields(): Observable<Boolean> {
    let retListener: Subject<Boolean> = new Subject<Boolean>();

    this.fieldsWD = [];
    this.fields = [];
    if (this.employerDto && this.access.hasGrantOwner(ExaGrant.READ_FIELD, this.employerDto.id)) {
      this.fieldRest.getFieldsUsingPOST(this.employerDto.username, { size: 1000 }).subscribe(
        (fields) => {
          this.fieldsWD = fields.content;
          this.fieldsWD$.next(this.fieldsWD);
          this.fields = this.filterDeletedElements(this.fieldsWD);
          this.fields$.next(this.fields);

          retListener.next(true);
        },
        (err) => {
          this.fieldsWD = [];
          this.fieldsWD$.next(this.fieldsWD);
          this.fields = [];
          this.fields$.next(this.fields);
          this.errorHandler.handleResponseError(err);

          retListener.next(false);
        }
      );
    } else {
      this.fields$.next(this.fields);
      this.fieldsWD$.next(this.fieldsWD);

      retListener.next(false);
    }

    return retListener;
  }

  private filterDeletedElements(elementsWithDeleted: FieldV2Dto[]): FieldV2Dto[] {
    return elementsWithDeleted.filter((elem) => !elem.deleted);
  }

  private filterNonDeletedElements(elementsWithDeleted: FieldV2Dto[]): FieldV2Dto[] {
    return elementsWithDeleted.filter((elem) => elem.deleted);
  }

  onGetFields(withDeleted?: boolean): Observable<FieldV2Dto[]> {
    this.loadFields();
    if (withDeleted) {
      return this.fieldsWD$;
    }
    return this.fields$;
  }

  getFieldsInactive(): FieldV2Dto[] {
    return this.filterNonDeletedElements(this.fieldsWD);
  }

  getFieldById(fieldId: number): FieldV2Dto {
    if (fieldId) {
      for (let field of this.fieldsWD) {
        if (field.id == fieldId) {
          return field;
        }
      }
    }
    return null;
  }

  getFieldsByCustomerId(customerId: number): FieldV2Dto[] {
    const fields: FieldV2Dto[] = [];
    if (customerId) {
      for (let field of this.fieldsWD) {
        if (field.customer.id == customerId) {
          fields.push(field);
        }
      }
    }
    return fields;
  }

  createItem(item: FieldV2Dto): Promise<FieldV2Dto> {
    return firstValueFrom(this.fieldRest.addFieldUsingPOST(item.owner!.username!, item));
  }

  addField(fieldDto: FieldV2Dto): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.fieldRest.addFieldUsingPOST(fieldDto.owner.username, fieldDto).subscribe(
        (result) => {
          // success
          this.alertService.openMessageToast(this.translate.instant('FIELD.ADD.SUCCESS'));
          this.reloadFields();
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          reject(err);
        }
      );
    });
  }

  updateField(fieldDto: FieldV2Dto): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.fieldRest.updateFieldUsingPUT(fieldDto.id, fieldDto).subscribe(
        (result) => {
          // success
          this.alertService.openMessageToast(this.translate.instant('FIELD.EDIT.SUCCESS'));
          this.reloadFields();
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          reject(err);
        }
      );
    });
  }

  updateFieldGeometry(fieldDto: FieldV2Dto, geometry: FieldGeometryDto): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.fieldRest.updateFieldGeometryUsingPUT(fieldDto.id, geometry).subscribe(
        (result) => {
          // success
          this.alertService.openMessageToast(this.translate.instant('FIELD.EDIT.SUCCESS'));
          this.reloadFields();
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          reject(err);
        }
      );
    });
  }

  deleteField(id: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.fieldRest.deleteFieldUsingDELETE(id).subscribe(
        (result) => {
          // Success
          this.alertService.openMessageToast(this.translate.instant('FIELD.DELETE.SUCCESS'));
          console.log(' FieldRest.deletFieldUsingDELETE -> SUCCESS');
          this.reloadFields();
          resolve();
        },
        (err) => {
          this.errorHandler.handleResponseError(err);
          console.log(' fieldRest.deleteFieldUsingDELETE -> ERROR');
          reject(err);
        }
      );
    });
  }

  getFormattedNameById(fieldId: number): string {
    if (fieldId) {
      const field: FieldV2Dto = this.getFieldById(fieldId);
      if (field) {
        return field.name;
      }
    }
    return '';
  }

  getFormattedName(field: FieldV2Dto): string {
    if (field) {
      return field.name;
    }
    return '';
  }

  updateItemGeometry(item: FieldV2Dto): Promise<FieldV2Dto> {
    return firstValueFrom(this.fieldRest.updateFieldGeometryUsingPUT(item.id!, item.geometry!));
  }
}
